import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Percent, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { dashboardAPI, gamesAPI } from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import { toast } from 'sonner';

export default function RakeRevenue() {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        dashboardAPI.getStats('30days'),
        gamesAPI.getPlatformConfig(),
      ]);
      setStats(s.data);
      setConfig(c.data);
    } catch {
      toast.error('Failed to load revenue');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setSaving(true);
      await gamesAPI.updatePlatformConfig({
        rake_percentage: Number(config.rake_percentage),
        rake_floor: Number(config.rake_floor),
        rake_ceiling: Number(config.rake_ceiling),
      });
      toast.success('Rake settings saved');
      load();
    } catch {
      toast.error('Could not save rake settings');
    } finally {
      setSaving(false);
    }
  };

  const variants = stats?.variants || [];

  return (
    <div data-testid="rake-page">
      <div className="page-header">
        <h1 className="page-title">Revenue</h1>
        <p className="page-subtitle">Rake collected from real games, and the rate the engine uses</p>
      </div>

      <div className="widget-grid mb-6">
        <div className="stat-card">
          <DollarSign className="w-5 h-5 text-primary mb-2" />
          <p className="stat-value">{formatCurrency(stats?.today_rake || 0)}</p>
          <p className="stat-label">Today's rake</p>
        </div>
        <div className="stat-card">
          <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="stat-value">{formatCurrency(stats?.month_rake || stats?.total_rake || 0)}</p>
          <p className="stat-label">Last 30 days</p>
        </div>
        <div className="stat-card">
          <Percent className="w-5 h-5 text-blue-400 mb-2" />
          <p className="stat-value">{config?.rake_percentage ?? '—'}%</p>
          <p className="stat-label">Live rake rate</p>
        </div>
        <div className="stat-card">
          <DollarSign className="w-5 h-5 text-amber-400 mb-2" />
          <p className="stat-value">{formatCurrency(stats?.gst_collected || 0)}</p>
          <p className="stat-label">GST on period rake (28%)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Rake settings
            </CardTitle>
            <CardDescription>Saved to platform config and read by the game engine</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Rake percentage</Label>
              <Input
                type="number"
                step="0.1"
                value={config?.rake_percentage ?? ''}
                onChange={(e) => setConfig({ ...config, rake_percentage: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Floor (₹)</Label>
                <Input
                  type="number"
                  value={config?.rake_floor ?? ''}
                  onChange={(e) => setConfig({ ...config, rake_floor: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Ceiling (₹)</Label>
                <Input
                  type="number"
                  value={config?.rake_ceiling ?? ''}
                  onChange={(e) => setConfig({ ...config, rake_ceiling: e.target.value })}
                />
              </div>
            </div>
            <Button onClick={save} disabled={!config || saving}>{saving ? 'Saving...' : 'Save rake'}</Button>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Money in the period</CardTitle>
            <CardDescription>Last 30 days from approved deposits, payouts, and bonuses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label="Approved deposits" value={formatCurrency(stats?.total_deposits || 0)} />
            <Row label="Approved withdrawals" value={formatCurrency(stats?.total_withdrawals || 0)} />
            <Row label="Net flow" value={formatCurrency(stats?.net_flow || 0)} />
            <Row label="Net revenue after GST" value={formatCurrency(stats?.net_revenue || 0)} />
            <Row label="Bonuses issued" value={formatCurrency(stats?.total_bonus_issued || 0)} />
            <Row label="Player deposit balances" value={formatCurrency(stats?.deposit_liability || 0)} />
            <Row label="Player winnings balances" value={formatCurrency(stats?.winnings_liability || 0)} />
          </CardContent>
        </Card>

        <Card className="bg-card border-border md:col-span-2">
          <CardHeader>
            <CardTitle>Games by mode</CardTitle>
            <CardDescription>Completed tables in the last 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            {variants.length === 0 ? (
              <p className="text-sm text-muted-foreground">No completed games in this period.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {variants.map((variant) => (
                  <div key={variant.name} className="p-4 bg-muted rounded-lg">
                    <p className="font-medium mb-3 capitalize">{String(variant.name).replace(/_/g, ' ')}</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Games</span>
                      <span className="font-mono">{formatNumber(variant.games)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-muted-foreground">Entry fees</span>
                      <span className="font-mono">{formatCurrency(variant.fees)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="font-mono font-medium">{value}</span>
    </div>
  );
}
